import React, { useState } from 'react';
import {
  Stack,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  CircularProgress,
} from '@mui/material';
import { ChipBadge } from 'new/elements/ChipBadge';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  PlayArrow as ActivateIcon,
  Pause as PauseIcon,
  Visibility as ViewDetailsIcon,
  Sync as SyncIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { SearchRounded } from '@mui/icons-material';
import { ReactComponent as ToolCropIcon } from 'new/components/icons/tool.svg';
import { ReactComponent as InfoSvg } from 'new/components/icons/fi-rr-info.svg';
import { Button } from 'new/elements/Button';
import { TextField } from 'new/elements/TextField';
import Typography from 'new/elements/Typography';
import CardsListPageNumbers from 'js/components/elements/cards/CardsListPageNumbers';
import { ToolsetCardSkeleton, SkeletonList } from '../AIToolsSection';
import { useURLState } from 'new/hooks/useURLState';
import { ToolDetailsDrawer } from './ToolDetailsDrawer';
import { CreateToolFromNexsetDialog } from './CreateToolFromNexsetDialog';
import { formatRelativeTime } from '../../utils/dateFormatters';
import { useSnackbar } from 'new/contexts/SnackbarContext';
import { useConfirmDialog } from 'new/elements/ConfirmDialog';
import { ErrorState } from 'new/elements/ErrorState';
import { EmptyStatePresets } from '../../components/EmptyState';
import useAppDispatch from 'js/hooks/useAppDispatch';
import {
  aiApi,
  useGetToolsQuery,
  useCreateToolFromNexsetMutation,
  useActivateToolMutation,
  usePauseToolMutation,
  useDeleteToolMutation,
  useSyncToolMutation,
} from '../../state/aiApi';
import type { ApiTool } from '../../state/aiApiTypes';
import { getStatusChipColor } from 'new/modules/ai/utils/statusColorHelpers';
import {
  getStatusLabel,
  getToolKindLabel,
  getToolDisplayName,
  type StatusFilter,
} from '../../utils/toolLabels';
import { useListFiltering } from '../../hooks/useListFiltering';
import { extractApiError } from '../../utils/errorUtils';
import { isToolAlreadyActive } from '../../utils/toolLifecycle';
import styles from './ToolsTab.module.scss';

const EMPTY_TOOLS: ApiTool[] = [];

// Tool Card Component
const ToolCard: React.FC<{
  tool: ApiTool;
  onDelete: (toolId: number) => void;
  onActivate: (toolId: number) => void;
  onPause: (toolId: number) => void;
  onOpenDetails: (tool: ApiTool) => void;
  onSync: (tool: ApiTool) => void;
  isSyncing: boolean;
}> = ({ tool, onDelete, onActivate, onPause, onOpenDetails, onSync, isSyncing }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const isActive = tool.status === 'active';
  const kindLabel = getToolKindLabel(tool.kind);

  return (
    <div className={styles.toolCard}>
      <div className={styles.toolCardContent}>
        <div className={styles.toolCardHeader}>
          <div className={styles.toolIcon}>
            <ToolCropIcon />
          </div>

          <div className={styles.toolInfo}>
            <Typography
              variant="body2"
              fontWeight={500}
              className={styles.toolName}
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetails(tool);
              }}
            >
              {getToolDisplayName(tool)}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {tool.description && (
                <Typography variant="caption" color="text.secondary" className={styles.description}>
                  {tool.description}
                </Typography>
              )}
              {tool.updated_at && (
                <Typography variant="caption" color="text.secondary" className={styles.updatedTime}>
                  {tool.description ? ' · ' : ''}Updated {formatRelativeTime(tool.updated_at)}
                </Typography>
              )}
            </Stack>
            {tool.key && (
              <Typography variant="caption" color="text.secondary" className={styles.toolKey}>
                <CodeIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                {tool.key}
              </Typography>
            )}
          </div>

          <div className={styles.badges}>
            {tool.access_mode === 'marketplace' && (
              <ChipBadge label="Data Product" color="secondary" variant="outlined" />
            )}
            {kindLabel && <ChipBadge label={kindLabel} variant="outlined" />}
            {tool.status && (
              <ChipBadge
                label={getStatusLabel(tool.status)}
                color={getStatusChipColor(tool.status)}
              />
            )}
          </div>

          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetails(tool);
              }}
              className={styles.infoButton}
            >
              <InfoSvg className={styles.infoIcon} />
            </IconButton>
          </Tooltip>

          <div className={styles.toolActions} onClick={(e) => e.stopPropagation()}>
            <IconButton size="small" onClick={handleMenuOpen} className={styles.actionButton}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem
                onClick={() => {
                  onOpenDetails(tool);
                  handleMenuClose();
                }}
              >
                <ViewDetailsIcon fontSize="small" className={styles.menuIcon} />
                View Details
              </MenuItem>
              {tool.kind === 'nexset_read' && (
                <MenuItem
                  onClick={() => {
                    onSync(tool);
                    handleMenuClose();
                  }}
                  disabled={isSyncing}
                >
                  {isSyncing ? (
                    <CircularProgress size={16} className={styles.menuIcon} />
                  ) : (
                    <SyncIcon fontSize="small" className={styles.menuIcon} />
                  )}
                  Sync from Nexset
                </MenuItem>
              )}
              <Divider />
              {!isActive && (
                <MenuItem
                  onClick={() => {
                    onActivate(tool.id);
                    handleMenuClose();
                  }}
                >
                  <ActivateIcon fontSize="small" className={styles.menuIcon} />
                  Activate
                </MenuItem>
              )}
              {isActive && (
                <MenuItem
                  onClick={() => {
                    onPause(tool.id);
                    handleMenuClose();
                  }}
                >
                  <PauseIcon fontSize="small" className={styles.menuIcon} />
                  Pause
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  navigator.clipboard.writeText(String(tool.id));
                  handleMenuClose();
                }}
              >
                <CopyIcon fontSize="small" className={styles.menuIcon} />
                Copy ID
              </MenuItem>
              {/* <Divider />
              <MenuItem
                onClick={() => {
                  onDelete(tool.id);
                  handleMenuClose();
                }}
                className={styles.deleteMenuItem}
              >
                <DeleteIcon fontSize="small" className={styles.menuIcon} />
                Delete
              </MenuItem> */}
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ToolsTab: React.FC = () => {
  const { getParam, setParam } = useURLState();
  const dispatch = useAppDispatch();
  const { showSuccess, showError } = useSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  // URL state for create dialog
  const createDialogOpen = getParam('modal') === 'create-tool';

  // Details drawer state
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedToolForDetails, setSelectedToolForDetails] = useState<ApiTool | null>(null);

  // RTK Query
  const {
    data: tools = EMPTY_TOOLS,
    isLoading,
    error,
    refetch,
  } = useGetToolsQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const [createTool, { isLoading: isCreating, error: createError, reset: resetCreate }] =
    useCreateToolFromNexsetMutation();
  const [activateTool] = useActivateToolMutation();
  const [pauseTool] = usePauseToolMutation();
  const [deleteTool] = useDeleteToolMutation();
  const [syncTool] = useSyncToolMutation();
  const [syncingToolIds, setSyncingToolIds] = useState<Set<number>>(new Set());

  // Client-side search, status filtering, and pagination
  const {
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    paginatedItems: paginatedTools,
    totalItems,
    totalPages,
    safePage,
    handlePageChange,
    statusCounts,
    clearFilters,
  } = useListFiltering({
    items: tools,
    searchFields: (t) => [t.name, t.description, t.key],
  });

  const handleDeleteTool = (toolId: number) => {
    const tool = tools.find((t) => t.id === toolId);
    confirm({
      title: 'Delete Tool',
      message: `Are you sure you want to delete "${tool?.name || 'this tool'}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await deleteTool(toolId).unwrap();
          showSuccess('Tool deleted successfully');
        } catch (err) {
          showError(extractApiError(err, 'Failed to delete tool'));
        }
      },
    });
  };

  const handleActivateTool = async (toolId: number) => {
    try {
      await activateTool(toolId).unwrap();
      showSuccess('Tool activated successfully');
    } catch (err) {
      showError(extractApiError(err, 'Failed to activate tool'));
    }
  };

  const handlePauseTool = async (toolId: number) => {
    try {
      await pauseTool(toolId).unwrap();
      showSuccess('Tool paused successfully');
    } catch (err) {
      showError(extractApiError(err, 'Failed to pause tool'));
    }
  };

  const handleSyncTool = async (tool: ApiTool) => {
    if (!tool.data_set_id) return;
    setSyncingToolIds((prev) => new Set(prev).add(tool.id));
    try {
      const result = await syncTool({ toolId: tool.id, nexset_id: tool.data_set_id }).unwrap();
      if (result.created_new_version) {
        showSuccess(`Tool "${tool.name}" synced — updated to v${result.version}`);
      } else {
        showSuccess(`Tool "${tool.name}" synced — already up to date`);
      }
    } catch (err) {
      showError(extractApiError(err, `Failed to sync tool "${tool.name}"`));
    } finally {
      setSyncingToolIds((prev) => {
        const next = new Set(prev);
        next.delete(tool.id);
        return next;
      });
    }
  };

  const handleCreateTool = async (data: { nexset_id: number }) => {
    try {
      const result = await createTool({ ...data, publish: true }).unwrap();
      // Two status vocabularies are in play here — see isToolAlreadyActive.
      // Only a draft result needs an explicit activate, and that call is
      // OPERATOR-only, so a Marketplace grantee must never reach it.
      const alreadyActive = isToolAlreadyActive(result);
      let activated = alreadyActive;
      if (!alreadyActive) {
        try {
          await activateTool(result.tool_id).unwrap();
          activated = true;
        } catch {
          // Activation failed but tool was created — force a refetch so the
          // list reflects the created (but inactive) tool
          dispatch(aiApi.util.invalidateTags(['ToolList']));
        }
      }
      setParam('modal', null);
      showSuccess(
        result.created_new_tool
          ? activated
            ? 'Tool created successfully'
            : 'Tool created but could not be activated'
          : activated
            ? 'Tool activated successfully'
            : 'Could not activate existing tool',
      );
      // Open details drawer so the user can see the tool immediately
      // (it may fall outside the paginated list window)
      setSelectedToolForDetails({ id: result.tool_id } as ApiTool);
      setDetailsDrawerOpen(true);
    } catch (err) {
      throw err;
    }
  };

  const handleOpenDetails = (tool: ApiTool) => {
    setSelectedToolForDetails(tool);
    setDetailsDrawerOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDrawerOpen(false);
    setSelectedToolForDetails(null);
  };

  const errorMessage = error ? extractApiError(error, 'Failed to load tools') : null;

  return (
    <Stack className={styles.container}>
      {/* Controls Bar */}
      <Stack direction="row" className={styles.controlsContainer}>
        <Stack direction="row" gap="16px" alignItems="center" flex={1}>
          <TextField
            placeholder="Search tools..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            startAdornment={<SearchRounded className={styles.searchIcon} />}
            className={styles.searchField}
          />
        </Stack>
        <Stack direction="row" gap="16px" alignItems="center">
          <CardsListPageNumbers
            isLoading={isLoading}
            currentPage={safePage}
            totalPages={totalPages}
            totalItems={totalItems}
            onListingChange={handlePageChange}
            disableHistoryUpdate
          />
          <Button
            variant="contained"
            color="primary"
            className={styles.addToolButton}
            startIcon={<AddIcon />}
            onClick={() => setParam('modal', 'create-tool')}
          >
            Create Tool
          </Button>
        </Stack>
      </Stack>

      {/* Content */}
      {isLoading ? (
        <div className={styles.listContainer}>
          <Stack spacing={1.5} className={styles.content}>
            <SkeletonList count={5} component={ToolsetCardSkeleton} />
          </Stack>
        </div>
      ) : errorMessage ? (
        <ErrorState
          message="Failed to load tools"
          description={errorMessage}
          onRetry={refetch}
          isRetrying={isLoading}
        />
      ) : totalItems === 0 ? (
        searchQuery ? (
          <EmptyStatePresets.NoSearchResults
            searchQuery={searchQuery}
            onClearSearch={() => setSearchQuery('')}
          />
        ) : (
          <EmptyStatePresets.NoTools onCreateTool={() => setParam('modal', 'create-tool')} />
        )
      ) : (
        <div className={styles.listContainer}>
          <Stack className={styles.content}>
            {paginatedTools.map((tool) => (
              <ToolCard
                key={tool.id}
                tool={tool}
                onDelete={handleDeleteTool}
                onActivate={handleActivateTool}
                onPause={handlePauseTool}
                onOpenDetails={handleOpenDetails}
                onSync={handleSyncTool}
                isSyncing={syncingToolIds.has(tool.id)}
              />
            ))}
          </Stack>
        </div>
      )}

      {/* Create Tool from Nexset Dialog */}
      <CreateToolFromNexsetDialog
        open={createDialogOpen}
        onClose={() => {
          setParam('modal', null);
          resetCreate();
        }}
        onSubmit={handleCreateTool}
        isLoading={isCreating}
        error={createError ? extractApiError(createError, 'Failed to create tool') : null}
      />

      {ConfirmDialogComponent}

      {/* Details Drawer */}
      <ToolDetailsDrawer
        toolId={selectedToolForDetails?.id ?? null}
        open={detailsDrawerOpen}
        onClose={handleCloseDetails}
      />
    </Stack>
  );
};
